import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';

import useEventTracking from '@hooks/useEventTracking';
import { HeaderProps, TI50Props } from '@interfaces/ti50';
import { ContentType, ImpressionElement, Page, impression } from '@tracking';
import { initializeAnchorLinks } from '@utils/anchorLinks';

import MainContent from './MainContent';
import Paywall from './Paywall';
import SEOContent from './SEOContent';
import CategoryHeader from './TIFifty2025/CategoryHeader';

interface LayoutProps extends TI50Props {
  Header: React.FC<HeaderProps>;
}

const Layout = ({
  activeCategories,
  className,
  companyNames,
  data,
  Header,
  isCurrentUserCorpOrGroupSubscriber,
  isSEOBot,
  paywallData,
  project,
  TI50Year,
  unlocked,
}: LayoutProps) => {
  const { Track, trackEvent } = useEventTracking({
    contentId: project.id,
    contentType: ContentType.Project,
    page: Page.Project,
    pageId: project.id,
  });
  const [showCategoryNav, setShowCategoryNav] = useState(false);

  const companyNamesOptions = companyNames.map((company) => {
    return { label: company, value: company };
  });

  useEffect(() => {
    initializeAnchorLinks();
    if (location.hash) {
      const sectionEl = document.getElementById(location.hash.replace('#', ''));
      if (sectionEl) {
        sectionEl.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  useEffect(() => {
    const section = document.getElementById('header-section');
    if (!section || !unlocked || TI50Year !== 2025) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowCategoryNav(!entry.isIntersecting),
      { rootMargin: '-20px 0px 0px 0px' }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [unlocked, TI50Year]);

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.Project }));
        }}
      >
        <div id={`ti-wrapper-ti50${TI50Year}`} className={clsx(`ti-50-${TI50Year}`, className)}>
          <Header
            activeCategories={activeCategories}
            companyNamesOptions={companyNamesOptions}
            isSEOBot={isSEOBot}
            project={project}
            unlocked={unlocked}
          />
          {showCategoryNav && <CategoryHeader activeCategories={activeCategories} />}
          {isSEOBot ? (
            <SEOContent activeCategories={activeCategories} data={data} />
          ) : unlocked ? (
            <MainContent
              activeCategories={activeCategories}
              data={data}
              isCurrentUserCorpOrGroupSubscriber={isCurrentUserCorpOrGroupSubscriber}
              project={project}
              TI50Year={TI50Year}
            />
          ) : (
            <Paywall paywallData={paywallData} leadType={project.leadType} />
          )}
        </div>
      </InView>
    </Track>
  );
};

export default Layout;
